import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, DollarSign, Calendar, Users, Clock, BarChart3, Download, RefreshCw } from "lucide-react";

const RevenueAnalytics = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId');
  
  const [venue, setVenue] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [occupancy, setOccupancy] = useState(null);
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30*24*60*60*1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchVenues();
  }, [user]);

  useEffect(() => {
    if (venueId) {
      fetchData();
    }
  }, [venueId, dateRange]);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/venues`);
      setVenues(response.data);
      if (response.data.length > 0 && !venueId) {
        const firstVenueId = response.data[0].id;
        navigate(`/revenue-analytics?venueId=${firstVenueId}`, { replace: true });
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [venueRes, analyticsRes, occupancyRes, customersRes] = await Promise.all([
        axios.get(`${API}/venues/${venueId}`),
        axios.get(`${API}/venue-owner/analytics/revenue`, {
          params: { venue_id: venueId, start_date: dateRange.startDate, end_date: dateRange.endDate }
        }),
        axios.get(`${API}/venue-owner/analytics/occupancy`, {
          params: { venue_id: venueId, start_date: dateRange.startDate, end_date: dateRange.endDate }
        }),
        axios.get(`${API}/venue-owner/analytics/customers`, {
          params: { venue_id: venueId }
        })
      ]);
      
      setVenue(venueRes.data);
      setAnalytics(analyticsRes.data);
      setOccupancy(occupancyRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error("Error fetching analytics:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const getTopHour = () => {
    if (!occupancy?.byHour) return "N/A";
    const entries = Object.entries(occupancy.byHour);
    if (entries.length === 0) return "N/A";
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return `${sorted[0][0]}:00 (${sorted[0][1]} rezervasyon)`;
  };

  const getTopDay = () => {
    if (!occupancy?.byDayOfWeek) return "N/A";
    const entries = Object.entries(occupancy.byDayOfWeek);
    if (entries.length === 0) return "N/A";
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return `${sorted[0][0]} (${sorted[0][1]} rezervasyon)`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <BarChart3 className="h-8 w-8 text-emerald-600" />
                Gelir Analizi
              </h1>
              {venue && <p className="text-gray-600 mt-1">{venue.name}</p>}
            </div>

            <div className="flex gap-2">
              {venues.length > 1 && (
                <Select value={venueId || ''} onValueChange={(value) => navigate(`?venueId=${value}`)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Saha seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {venues.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button variant="outline" onClick={fetchData}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Yenile
              </Button>
            </div>
          </div>

          {/* Date Range Filter */}
          <Card>
            <CardContent className="p-4">
              <div className="flex gap-4 items-end">
                <div className="flex-1">
                  <Label>Başlangıç Tarihi</Label>
                  <Input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                  />
                </div>
                <div className="flex-1">
                  <Label>Bitiş Tarihi</Label>
                  <Input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                  />
                </div>
                <Button onClick={fetchData}>Filtrele</Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Summary Cards */}
        {analytics && (
          <div className="grid md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600">Toplam Gelir</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-emerald-600">
                  {analytics.summary.totalRevenue.toLocaleString('tr-TR')} TL
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Ödenen: {analytics.summary.paidRevenue.toLocaleString('tr-TR')} TL
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600">Rezervasyon Sayısı</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">
                  {analytics.summary.totalBookings}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Onaylı: {analytics.summary.confirmedBookings}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600">Doluluk Oranı</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">
                  %{analytics.summary.occupancyRate.toFixed(1)}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Ortalama doluluk
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-600">Bekleyen Ödeme</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-yellow-600">
                  {analytics.summary.pendingRevenue.toLocaleString('tr-TR')} TL
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Tahsil edilecek
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Revenue by Day Chart */}
          {analytics?.revenueByDay && (
            <Card>
              <CardHeader>
                <CardTitle>Günlük Gelir Grafiği</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(analytics.revenueByDay)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .slice(-14)
                    .map(([date, revenue]) => (
                      <div key={date} className="flex items-center gap-2">
                        <div className="w-24 text-sm">{new Date(date).toLocaleDateString('tr-TR', {day: 'numeric', month: 'short'})}</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-6">
                          <div 
                            className="bg-emerald-500 h-6 rounded-full flex items-center justify-end pr-2 text-xs text-white font-semibold"
                            style={{width: `${Math.min((revenue / Math.max(...Object.values(analytics.revenueByDay))) * 100, 100)}%`}}
                          >
                            {revenue > 0 && `${revenue} TL`}
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          )}

          {/* Revenue by Hour */}
          {analytics?.revenueByHour && (
            <Card>
              <CardHeader>
                <CardTitle>Saatlere Göre Gelir</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {Object.entries(analytics.revenueByHour)
                    .sort((a, b) => a[0].localeCompare(b[0]))
                    .map(([hour, revenue]) => (
                      <div key={hour} className="flex items-center gap-2">
                        <div className="w-16 text-sm">{hour}:00</div>
                        <div className="flex-1 bg-gray-200 rounded-full h-6">
                          <div 
                            className="bg-blue-500 h-6 rounded-full flex items-center justify-end pr-2 text-xs text-white font-semibold"
                            style={{width: `${Math.min((revenue / Math.max(...Object.values(analytics.revenueByHour))) * 100, 100)}%`}}
                          >
                            {revenue > 0 && `${revenue} TL`}
                          </div>
                        </div>
                      </div>
                    ))
                  }
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Occupancy Analysis */}
          {occupancy && (
            <Card>
              <CardHeader>
                <CardTitle>Doluluk Analizi</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2">En Yoğun Saat</h4>
                  <Badge className="text-lg">{getTopHour()}</Badge>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">En Yoğun Gün</h4>
                  <Badge className="text-lg" variant="outline">{getTopDay()}</Badge>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Haftalık Dağılım</h4>
                  <div className="space-y-1">
                    {Object.entries(occupancy.byDayOfWeek || {}).map(([day, count]) => (
                      <div key={day} className="flex justify-between text-sm">
                        <span>{day}</span>
                        <span className="font-semibold">{count} rezervasyon</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Customers */}
          {customers && (
            <Card>
              <CardHeader>
                <CardTitle>En İyi Müşteriler</CardTitle>
                <CardDescription>Toplam {customers.totalCustomers} müşteri</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {customers.topCustomers.slice(0, 10).map((customer, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-emerald-600 rounded-full flex items-center justify-center text-white font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <div className="font-semibold">{customer.userName}</div>
                          <div className="text-sm text-gray-600">{customer.bookingCount} rezervasyon</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-emerald-600">{customer.totalSpent.toLocaleString('tr-TR')} TL</div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default RevenueAnalytics;
